import { TimezoneSelect } from "@themes/pollinator/ui/TimezoneSelect";
import type { TimezoneOption } from "@themes/pollinator/ui/TimezoneSelect";
import { SubStepLayout } from "@platform/features/onboarding/components/SubStepLayout";

export type Props = {
  /** Available timezone options */
  timezoneOptions: TimezoneOption[];
  /** Currently selected timezone */
  selectedTimezone: TimezoneOption | null;
  /** Handler when timezone selection changes */
  onTimezoneChange: (timezone: TimezoneOption | null) => void;
  /** Handler for continue/next button */
  onContinue: () => void;
  /** Handler for back button (hidden if undefined) */
  onBack?: () => void;
  /** Handler for skip button (hidden if undefined) */
  onSkip?: () => void;
  /** Current step index (0-indexed) */
  stepIndex: number;
  /** Total steps */
  totalSteps: number;
  /** Whether primary button is disabled */
  isPrimaryDisabled?: boolean;
};

/**
 * Presentational timezone selection sub-step for profile onboarding.
 * Renders a timezone selector within the sub-step layout.
 */
export function TimezoneSubStep({
  timezoneOptions,
  selectedTimezone,
  onTimezoneChange,
  onContinue,
  onBack,
  onSkip,
  stepIndex,
  totalSteps,
  isPrimaryDisabled,
}: Props) {
  const handleTimezoneChange = (
    val: TimezoneOption | TimezoneOption[] | null
  ) => {
    // TimezoneSelect can return array for multi-select, but we use single
    if (Array.isArray(val)) {
      onTimezoneChange(val[0] ?? null);
    } else {
      onTimezoneChange(val);
    }
  };

  return (
    <SubStepLayout
      title="Select Your Timezone"
      description="This helps us schedule meetings at times that work for you."
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      onPrimaryClick={onContinue}
      onBack={onBack}
      onSkip={onSkip}
      isPrimaryDisabled={isPrimaryDisabled ?? !selectedTimezone}
    >
      <div className="flex flex-col gap-4">
        <TimezoneSelect
          name="timezone"
          options={timezoneOptions}
          value={selectedTimezone}
          onChange={handleTimezoneChange}
          placeholder="Select your timezone..."
        />
      </div>
    </SubStepLayout>
  );
}

export default TimezoneSubStep;
